/**
 * Shared BroadcastChannel for syncing state between the main app window
 * and tool windows (SPARQL editor, Rule Editor, Ontology Editor/Viewer).
 */

import { useState, useEffect } from 'react'

export type ToolSyncMessage =
  | { type: 'repo-update'; repoURL: string; localRepoURL?: string }
  | { type: 'request-repo' }
  | { type: 'repo-response'; repoURL: string; localRepoURL?: string }

const CHANNEL_NAME = 'tool-sync'

let _channel: BroadcastChannel | null = null

export function getToolSyncChannel(): BroadcastChannel {
  if (!_channel) {
    _channel = new BroadcastChannel(CHANNEL_NAME)
  }
  return _channel
}

/**
 * React hook for child/tool windows to subscribe to repoURL updates
 * from the main app. Falls back to the initial values from URL params.
 *
 * On mount, it sends a `request-repo` message to the main app.
 * The main app responds with `repo-response`, and also broadcasts
 * `repo-update` when the repoURL changes.
 */
export function useToolSyncRepo(initialRepoURL: string, initialLocalRepoURL?: string) {
  const [repoURL, setRepoURL] = useState(initialRepoURL)
  const [localRepoURL, setLocalRepoURL] = useState(initialLocalRepoURL)

  useEffect(() => {
    const ch = getToolSyncChannel()

    const handler = (e: MessageEvent<ToolSyncMessage>) => {
      if (e.data.type === 'repo-update' || e.data.type === 'repo-response') {
        setRepoURL(e.data.repoURL)
        if (e.data.localRepoURL !== undefined) setLocalRepoURL(e.data.localRepoURL)
      }
    }

    ch.addEventListener('message', handler)

    // Request current repo from the main app
    ch.postMessage({ type: 'request-repo' } satisfies ToolSyncMessage)

    return () => ch.removeEventListener('message', handler)
  }, [])

  return { repoURL, localRepoURL }
}
